import * as React from 'react';
import { Slot } from '@/components/ui/Slot';
import clsx from 'clsx';

const BREADCRUMB_ROOT_NAME = 'BreadcrumbRoot';
const BREADCRUMB_ITEM_NAME = 'BreadcrumbItem';
const BREADCRUMB_ICON_NAME = 'BreadcrumbIcon';
const BREADCRUMB_ARROW_NAME = 'BreadcrumbArrow';

type BreadcrumbRootProps = React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
};

function BreadcrumbRoot({
  asChild,
  children,
  className,
  ...rest
}: BreadcrumbRootProps) {
  const Component = asChild ? Slot : 'div';

  return (
    <Component className={clsx('breadcrumb', className)} {...rest}>
      {children}
    </Component>
  );
}
BreadcrumbRoot.displayName = BREADCRUMB_ROOT_NAME;

type BreadcrumbItemProps = React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  active?: boolean;
};

const BreadcrumbItem = React.forwardRef<HTMLDivElement, BreadcrumbItemProps>(
  ({ asChild, children, className, active, ...rest }, forwardedRef) => {
    const Component = asChild ? Slot : 'div';

    return (
      <Component
        ref={forwardedRef}
        className={clsx(
          'breadcrumb__item',
          {
            'breadcrumb__item--active': active,
            'breadcrumb__item--inactive': !active,
          },
          className
        )}
        {...rest}
      >
        {children}
      </Component>
    );
  }
);
BreadcrumbItem.displayName = BREADCRUMB_ITEM_NAME;

type BreadcrumbIconProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: React.ElementType;
};

function BreadcrumbIcon<T extends React.ElementType>({
  className,
  as,
  ...rest
}: BreadcrumbIconProps & React.ComponentPropsWithoutRef<T>) {
  const Component = as || 'div';

  return (
    <Component className={clsx('breadcrumb__icon', className)} {...rest} />
  );
}
BreadcrumbIcon.displayName = BREADCRUMB_ICON_NAME;

type BreadcrumbArrowIconProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: React.ElementType;
};

function BreadcrumbArrowIcon<T extends React.ElementType>({
  className,
  as,
  ...rest
}: BreadcrumbArrowIconProps & React.ComponentPropsWithoutRef<T>) {
  const Component = as || 'div';

  return (
    <Component className={clsx('breadcrumb__arrow', className)} {...rest} />
  );
}
BreadcrumbArrowIcon.displayName = BREADCRUMB_ARROW_NAME;

// Breadcrumbs component with high-level API
interface BreadcrumbItem {
  label: string;
  href?: string;
  startIcon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  className?: string;
}

function Breadcrumbs({ items, separator, className }: BreadcrumbsProps) {
  return (
    <BreadcrumbRoot className={className}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isActive = isLast;

        return (
          <React.Fragment key={index}>
            <BreadcrumbItem active={isActive}>
              {item.startIcon && (
                <BreadcrumbIcon>{item.startIcon}</BreadcrumbIcon>
              )}
              {item.href ? (
                <a href={item.href} className="breadcrumb__link">
                  {item.label}
                </a>
              ) : (
                item.label
              )}
            </BreadcrumbItem>
            {!isLast && separator && (
              <BreadcrumbArrowIcon>{separator}</BreadcrumbArrowIcon>
            )}
          </React.Fragment>
        );
      })}
    </BreadcrumbRoot>
  );
}

export {
  BreadcrumbRoot as Root,
  BreadcrumbItem as Item,
  BreadcrumbIcon as Icon,
  BreadcrumbArrowIcon as ArrowIcon,
  Breadcrumbs,
};
